import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { range } from 'lodash';

const SuccessionDownPayment = ({ query, updateCfaQuery, currentUser, intl }) => {
  const [fixedPaymentTooHigh, setFixedPaymentTooHigh] = useState(false);
  const company = currentUser?.company;
  const showCheckSwap = company?.features?.includes('check_swap') || false;
  const showConsultingAgreement = company?.features?.includes('cash_flow_consulting_agreements') || false;
  
  // Calculate consulting annual compensation
  let consultingAnnualCompensation = 0;
  if (!isNaN(parseFloat(query.consultingTotalCompensation)) && 
      !isNaN(parseFloat(query.consultingLength)) && 
      parseFloat(query.consultingLength) !== 0) {
    consultingAnnualCompensation = (parseFloat(query.consultingTotalCompensation) / parseFloat(query.consultingLength)).toFixed(2);
  }
  consultingAnnualCompensation = isNaN(consultingAnnualCompensation) ? 0.00 : consultingAnnualCompensation;
  
  const validate = () => {
    let valid = true;

    // Down payment validation
    if (query.buyerHasLumpSumPayment == undefined) {
      valid = false;
    } else if (query.buyerHasLumpSumPayment) {
      if (!query.buyerLumpSumAmount || parseFloat(query.buyerLumpSumAmount) < 1) {
        valid = false;
      }
    }

    // Financing validation - only if down payment is enabled
    if (query.buyerHasLumpSumPayment) {
      if (query.buyerIsFinancing == undefined) {
        valid = false;
      } else if (query.buyerIsFinancing) {
        if (!query.buyersFinancingAmount || parseFloat(query.buyersFinancingAmount) < 1) {
          valid = false;
        }

        if (!query.buyerFinancingYears || parseFloat(query.buyerFinancingYears) < 1) {
          valid = false;
        }
      }
    }

    // Check swap validation - only if feature is enabled and selected
    if (showCheckSwap && query.hasCheckSwap) {
      if (!query.checkSwapYear || !query.checkSwapAnnualBonus || !query.checkSwapLoanEndYear || !query.checkSwapLoanRepaymentAmount) {
        valid = false;
      }
    } else if (!showCheckSwap) {
      // If check swap feature is not enabled, set default values
      if (query.hasCheckSwap === undefined) {
        updateCfaQuery({ ...query, hasCheckSwap: false });
      }
    }

    // Consulting agreement validation - only if feature is enabled
    if (showConsultingAgreement) {
      if (query.hasConsultingAgreement == undefined) {
        valid = false;
      } else if (query.hasConsultingAgreement) {
        if (!query.consultingTotalCompensation || parseFloat(query.consultingTotalCompensation) <= 0) {
          valid = false;
        }
    
        if (!query.consultingLength || parseInt(query.consultingLength) <= 0) {
          valid = false;
        }
      }
    } else {
      // If consulting agreement feature is not enabled, set default values
      if (query.hasConsultingAgreement === undefined) {
        updateCfaQuery({ ...query, hasConsultingAgreement: false });
      }
    }

    if (query.valid != valid) {
      updateCfaQuery({ ...query, valid });
    }
  };

  // Call validate on every render (matching original behavior)
  validate();

  const formatCurrency = (value) => {
    if (!value || value === '') return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">
        <FormattedMessage id="calculators.cfa.terms_of_deal" />
      </h2>
      <p className="text-gray-600 mb-8">
        <FormattedMessage id="calculators.cfa.terms_copy" />
      </p>
      
      <div className="space-y-8">
        {/* Down Payment Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">
            <FormattedMessage id="calculators.cfa.down_payment" />
          </h3>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <FormattedMessage id="calculators.cfa.down_payment_q" />
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="buyerHasLumpSumPayment"
                    checked={query.buyerHasLumpSumPayment === true}
                    onChange={() => {
                      const clear = query.buyerHasLumpSumPayment ? {} : {
                        buyerLumpSumAmount: 0,
                        buyersFinancingAmount: 0,
                        buyerIsFinancing: false,
                        buyerFinancingYears: 0,
                      };
                      updateCfaQuery({ ...query, ...clear, buyerHasLumpSumPayment: true });
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <FormattedMessage id="calculators.cfa.yes_label" />
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="buyerHasLumpSumPayment"
                    checked={query.buyerHasLumpSumPayment === false}
                    onChange={() => {
                      const clear = {
                        buyerLumpSumAmount: 0,
                        buyersFinancingAmount: 0,
                        buyerIsFinancing: false,
                        buyerFinancingYears: 0,
                      };
                      updateCfaQuery({ ...query, ...clear, buyerHasLumpSumPayment: false });
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <FormattedMessage id="calculators.cfa.no_label" />
                  </span>
                </label>
              </div>
            </div>

            {query.buyerHasLumpSumPayment && (
              <div className="space-y-4 ml-6 border-l-2 border-gray-200 pl-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FormattedMessage id="calculators.cfa.how_much_down" />
                  </label>
                  <div className="relative max-w-xs">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
                    <input
                      type="text"
                      value={query.buyerLumpSumAmount ? formatCurrency(query.buyerLumpSumAmount).replace('$', '').replace(/,/g, '') : ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        const numValue = parseFloat(value) || 0;
                        let financingAmount = query.buyersFinancingAmount;
                        if (financingAmount > numValue) {
                          financingAmount = numValue;
                        }
                        updateCfaQuery({ 
                          ...query, 
                          buyerLumpSumAmount: numValue,
                          buyersFinancingAmount: financingAmount
                        });
                      }}
                      className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FormattedMessage id="calculators.cfa.year_given" />
                  </label>
                  <select
                    value={query.buyerLumpSumYear || ''}
                    onChange={(e) => {
                      updateCfaQuery({ ...query, buyerLumpSumYear: parseInt(e.target.value) });
                    }}
                    className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">
                      {intl.formatMessage({ id: "calculators.cfa.please_select_a_year" })}
                    </option>
                    {range(1, query.cashFlowLength || 10).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Financing Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <FormattedMessage id="calculators.cfa.utilize_financing" />
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="buyerIsFinancing"
                    checked={query.buyerIsFinancing === true}
                    disabled={!query.buyerHasLumpSumPayment}
                    onChange={() => {
                      updateCfaQuery({ ...query, buyerIsFinancing: true });
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 disabled:opacity-50"
                  />
                  <span className={`ml-2 text-sm ${!query.buyerHasLumpSumPayment ? 'text-gray-400' : 'text-gray-700'}`}>
                    <FormattedMessage id="calculators.cfa.yes_label" />
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="buyerIsFinancing"
                    checked={query.buyerIsFinancing === false}
                    disabled={!query.buyerHasLumpSumPayment}
                    onChange={() => {
                      updateCfaQuery({ ...query, buyerIsFinancing: false, buyersFinancingAmount: 0 });
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 disabled:opacity-50"
                  />
                  <span className={`ml-2 text-sm ${!query.buyerHasLumpSumPayment ? 'text-gray-400' : 'text-gray-700'}`}>
                    <FormattedMessage id="calculators.cfa.no_label" />
                  </span>
                </label>
              </div>
            </div>

            {(query.buyerIsFinancing || query.buyersFinancingAmount > 0) && (
              <div className="space-y-4 ml-6 border-l-2 border-gray-200 pl-4">
                {fixedPaymentTooHigh && (
                  <div className="text-red-600 text-sm font-medium">
                    {intl.formatMessage({ id: 'succession.cash_flow_analysis.down_payment.finance_cannot_exceed' })}
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FormattedMessage id="calculators.cfa.how_much_to_finance" />
                  </label>
                  <div className="relative max-w-xs">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
                    <input
                      type="text"
                      value={query.buyersFinancingAmount ? formatCurrency(query.buyersFinancingAmount).replace('$', '').replace(/,/g, '') : ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        let numValue = parseFloat(value) || 0;
                        if (numValue > query.buyerLumpSumAmount) {
                          numValue = query.buyerLumpSumAmount;
                          setFixedPaymentTooHigh(true);
                        } else {
                          setFixedPaymentTooHigh(false);
                        }
                        updateCfaQuery({ ...query, buyersFinancingAmount: numValue });
                      }}
                      className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FormattedMessage id="calculators.cfa.how_many_years_to_finance" />
                  </label>
                  <select
                    value={query.buyerFinancingYears || ''}
                    onChange={(e) => {
                      updateCfaQuery({ ...query, buyerFinancingYears: parseInt(e.target.value) });
                    }}
                    className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">
                      {intl.formatMessage({ id: "calculators.cfa.please_select_a_year" })}
                    </option>
                    {range(1, 11).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Check Swap Section (feature-flagged) */}
        {showCheckSwap && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <FormattedMessage id="calculators.cfa.will_the_parties_utilize_a_check_swap" />
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hasCheckSwap"
                      checked={query.hasCheckSwap === true}
                      onChange={() => {
                        updateCfaQuery({ ...query, hasCheckSwap: true });
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      <FormattedMessage id="calculators.cfa.yes_label" />
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hasCheckSwap"
                      checked={query.hasCheckSwap === false}
                      onChange={() => {
                        updateCfaQuery({ ...query, hasCheckSwap: false });
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      <FormattedMessage id="calculators.cfa.no_label" />
                    </span>
                  </label>
                </div>
              </div>

              {query.hasCheckSwap && (
                <div className="space-y-4 ml-6 border-l-2 border-gray-200 pl-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FormattedMessage id="calculators.cfa.what_year_to_check_swap_to" />
                    </label>
                    <select
                      value={query.checkSwapYear || ''}
                      onChange={(e) => {
                        updateCfaQuery({ ...query, checkSwapYear: parseInt(e.target.value) });
                      }}
                      className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">
                        {intl.formatMessage({ id: "calculators.cfa.please_select_a_year" })}
                      </option>
                      {range(1, (query.cashFlowLength || 10) + 1).map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FormattedMessage id="calculators.cfa.what_is_the_check_swap_amount" />
                    </label>
                    <div className="relative max-w-xs">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
                      <input
                        type="text"
                        value={query.checkSwapAnnualBonus ? formatCurrency(query.checkSwapAnnualBonus).replace('$', '').replace(/,/g, '') : ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          updateCfaQuery({ ...query, checkSwapAnnualBonus: parseFloat(value) || 0 });
                        }}
                        className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FormattedMessage id="calculators.cfa.what_year_will_loan_end" />
                    </label>
                    <select
                      value={query.checkSwapLoanEndYear || ''}
                      onChange={(e) => {
                        updateCfaQuery({ ...query, checkSwapLoanEndYear: parseInt(e.target.value) });
                      }}
                      className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">
                        {intl.formatMessage({ id: "calculators.cfa.please_select_a_year" })}
                      </option>
                      {range(1, (query.cashFlowLength || 10) + 1).map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FormattedMessage id="calculators.cfa.what_is_the_loan_repayment_amount" />
                    </label>
                    <div className="relative max-w-xs">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
                      <input
                        type="text"
                        value={query.checkSwapLoanRepaymentAmount ? formatCurrency(query.checkSwapLoanRepaymentAmount).replace('$', '').replace(/,/g, '') : ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          updateCfaQuery({ ...query, checkSwapLoanRepaymentAmount: parseFloat(value) || 0 });
                        }}
                        className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Consulting Agreement Section (feature-flagged) */}
        {showConsultingAgreement && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <FormattedMessage id="calculators.cfa.will_the_parties_enter_into_a_consulting_agreement" />
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hasConsultingAgreement"
                      checked={query.hasConsultingAgreement === true}
                      onChange={() => {
                        updateCfaQuery({ ...query, hasConsultingAgreement: true });
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      <FormattedMessage id="calculators.cfa.yes_label" />
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hasConsultingAgreement"
                      checked={query.hasConsultingAgreement === false}
                      onChange={() => {
                        const clear = {
                          consultingTotalCompensation: 0,
                          consultingLength: 0,
                          consultingAnnualCompensation: 0,
                          consultingPercentOfDeal: 0,
                          consultingIncludedInListPrice: false,
                        };
                        updateCfaQuery({ ...query, ...clear, hasConsultingAgreement: false });
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      <FormattedMessage id="calculators.cfa.no_label" />
                    </span>
                  </label>
                </div>
              </div>

              {query.hasConsultingAgreement && (
                <div className="space-y-4 ml-6 border-l-2 border-gray-200 pl-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FormattedMessage id="calculators.cfa.what_is_the_total_compensation" />
                    </label>
                    <div className="relative max-w-xs">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
                      <input
                        type="text"
                        value={query.consultingTotalCompensation ? formatCurrency(query.consultingTotalCompensation).replace('$', '').replace(/,/g, '') : ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          updateCfaQuery({ ...query, consultingTotalCompensation: parseFloat(value) || 0 });
                        }}
                        className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FormattedMessage id="calculators.cfa.what_is_the_length_of_the_agreement" />
                    </label>
                    <select
                      value={query.consultingLength || ''}
                      onChange={(e) => {
                        updateCfaQuery({ ...query, consultingLength: parseInt(e.target.value) });
                      }}
                      className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">
                        {intl.formatMessage({ id: "calculators.cfa.please_select_a_year" })}
                      </option>
                      {range(1, query.cashFlowLength || 10).map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FormattedMessage id="calculators.cfa.what_is_the_annual_compensation" />
                    </label>
                    <div className="relative max-w-xs">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
                      <input
                        type="text"
                        value={formatCurrency(consultingAnnualCompensation).replace('$', '').replace(/,/g, '')}
                        disabled
                        className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 text-right"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default injectIntl(SuccessionDownPayment); 